<template>
  <article class="product-card-list group bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all">
    <div class="flex gap-4">
      <!-- Product image -->
      <NuxtLink :to="$localePath({ name: 'product-slug', params: { slug: product.slug } })" class="shrink-0">
        <div class="relative w-24 h-24 bg-gray-100 overflow-hidden rounded-lg">
          <img
            v-if="product.images.length"
            :src="product.images[0].url"
            :alt="product.name"
            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
          <img v-else src="/img/placeholder.jpg" alt="Placeholder" class="w-full h-full object-cover" />
        </div>
      </NuxtLink>

      <!-- Product info -->
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <!-- Title -->
            <NuxtLink :to="$localePath({ name: 'product-slug', params: { slug: product.slug } })" class="block">
              <h3 class="text-base font-semibold text-gray-900 mb-1 hover:text-primary transition-colors">
                {{ product.name }}
              </h3>
            </NuxtLink>

            <!-- Description -->
            <p class="text-sm text-gray-600 line-clamp-2 mb-2">
              {{ product.description }}
            </p>

            <!-- Rating -->
            <div v-if="product.rating" class="flex items-center gap-1 mb-2">
              <Icon name="i-heroicons-star-solid" class="w-4 h-4 text-yellow-400" />
              <span class="text-xs font-medium text-gray-700">{{ product.rating }}</span>
            </div>

            <!-- Price -->
            <div class="flex items-baseline gap-2">
              <Price :value="product.regularPrice"  class="text-lg font-bold" />
            </div>
          </div>

          <!-- Action buttons -->
          <div class="flex flex-col gap-2 items-end">
                  <div v-if="product.tags.length > 0" class="flex gap-2">
        <ProductTag v-for="tag in product.tags" :key="tag.name" :tag="tag" />
      </div>

            <!-- Wishlist button -->
            <button
              v-if="enableWishlist"
              @click.stop="toggleWishlist"
              class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 transition-all"
              :class="{ 'text-red-500 bg-red-50': wishlistItem, 'text-gray-600': !wishlistItem }"
              aria-label="Add to wishlist"
            >
              <Icon :name="wishlistItem ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'" class="w-5 h-5" />
            </button>

            <!-- Add to cart button -->
            <UButton @click.stop="addToCart" size="sm" icon="i-heroicons-shopping-cart">
              Add to cart
            </UButton>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Price from '~/components/molecules/Price.vue'
import { useCartStore } from '~/stores/useCartStore'
import { useWishlistStore } from '~/stores/useWishlistStore'
import ProductTag from '~/components/atoms/ProductTag.vue'

const props = withDefaults(
  defineProps<{
    product: any
    enableWishlist?: boolean
  }>(),
  {
    enableWishlist: true
  }
)

const { addProduct } = useCartStore()
const wishlist = useWishlistStore()
const toast = useToast()

const wishlistItem = computed(() => wishlist.isWishlisted(props.product.id))

function addToCart() {
  addProduct({ productId: props.product.id, quantity: 1 })

  toast.add({
    title: 'Added to cart',
    description: props.product.name,
    color: 'success'
  })
}

function toggleWishlist() {
  if (wishlistItem.value) {
    wishlist.removeItem(wishlistItem.value.id)

    toast.add({
      title: 'Removed from wishlist',
      description: props.product.name,
      color: 'warning'
    })
  } else {
    wishlist.addProduct(props.product.id)

    toast.add({
      title: 'Added to wishlist',
      description: props.product.name,
      color: 'success'
    })
  }
}
</script>
